import type Alpine from "alpinejs";

/**
 * Auto-applies a fade-in to every `.reveal` element on the page using
 * IntersectionObserver. Honours prefers-reduced-motion via CSS.
 *
 * Stagger: any `.reveal-stagger` parent gets `--reveal-delay` set on each
 * `.reveal` child based on its index (60 ms gap, capped at 500 ms).
 */
export function registerReveal(A: typeof Alpine) {
  A.directive("reveal", (el: HTMLElement) => {
    el.classList.add("reveal");
  });

  const STAGGER_STEP = 60;
  const STAGGER_CAP = 500;

  const applyStagger = () => {
    document
      .querySelectorAll<HTMLElement>(".reveal-stagger")
      .forEach((parent) => {
        const children = parent.querySelectorAll<HTMLElement>(
          ":scope > .reveal, :scope > * > .reveal",
        );
        children.forEach((child, i) => {
          const delay = Math.min(i * STAGGER_STEP, STAGGER_CAP);
          child.style.setProperty("--reveal-delay", `${delay}ms`);
        });
      });
  };

  const start = () => {
    applyStagger();
    const els = document.querySelectorAll<HTMLElement>(".reveal:not(.is-in)");
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((e) => e.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );
    els.forEach((el) => io.observe(el));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
